import { useRef } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "react-tooltip";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import CollegeCard from "./CollegeCard";
import DraggableItem from "./components/DraggableItem";
import ScrollingDropTarget from "./components/ScrollingDropTarget";
import useNavigation from "./hooks/useNavigation";
import useCollegesManager from "./hooks/useCollegesManager";

import { ItemsCounter } from "../../../core";
import { AffordabilitySquare } from "../../../core/compose/affordability";
import { ArrowNav, SmallStar } from "../../../core/icons";
import { GetIpedsId } from "../../Common/Hooks";

function CompareColleges({
  colleges,
  heights,
  favorite,
  ethnicity,
  className,
  studentState,
  sectionsState,
  selectAsFavorite,
  setGlobalHeights,
  unselectAsFavorite,
  showUploadTutorial,
  setGlobalSectionState,
  setNewSortOrderColleges
}) {
  const collegesContainerRef = useRef(null);
  const ipedsId = GetIpedsId();

  const { next, previous, navigateTo } = useNavigation(
    collegesContainerRef,
    ipedsId
  );
  const {
    collegesLocal,
    favoriteLocal,
    moveItem,
    selectAsFavoriteLocal,
    unselectAsFavoriteLocal
  } = useCollegesManager({
    colleges,
    favorite,
    ipedsId,
    selectAsFavorite,
    unselectAsFavorite,
    setNewSortOrderColleges
  });

  if (collegesLocal.length === 0 && !favoriteLocal) return null;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={className}>
        <div className="py-2">
          <div className="flex flex-1 flex-row">
            {favoriteLocal && (
              <div
                id="compare-colleges-favorite-container"
                className="mx-4 w-24rem min-w-24rem"
              >
                <h2 className="text-2xl font-bold">Compare Colleges</h2>
                <p className="text-sm font-bold capitalize">your favorite:</p>
              </div>
            )}
            <div className="flex flex-1 flex-col overflow-hidden">
              <div
                id="compare-colleges-container"
                className="flex flex-row items-center"
              >
                {!favoriteLocal && (
                  <h2 className="mr-5 text-2xl font-bold">Compare Colleges</h2>
                )}
                <button
                  onClick={previous}
                  className="my-1 py-1 px-2"
                  aria-label="arrow-left-nav"
                  id="compare-left-arrow-button"
                >
                  <ArrowNav size={20} />
                </button>
                <button
                  onClick={next}
                  aria-label="arrow-right-nav"
                  id="compare-right-arrow-button"
                  className="my-1 py-1 px-2 transform rotate-180"
                >
                  <ArrowNav size={20} />
                </button>
                <div className="ml-5 mr-2">
                  <ItemsCounter
                    pluralDescriptionItems="Colleges"
                    singularDescriptionItems="College"
                    numberItems={
                      favoriteLocal
                        ? collegesLocal.length + 1
                        : collegesLocal.length
                    }
                  />
                </div>
                <div
                  className="
                    flex
                    flex-row
                    divide-x-2
                    items-center
                    divide-black
                    overflow-hidden
                  "
                >
                  {favoriteLocal && (
                    <div id="school-favorite-icon">
                      <AffordabilitySquare
                        className="mr-2"
                        data-tooltip-id="college-thumbnail-squares"
                        data-tooltip-content={favoriteLocal?.school?.name}
                        coverage={
                          favoriteLocal.award?.postAwardSchoolCosts?.coverage
                        }
                        affordability={
                          favoriteLocal.award?.postAwardSchoolCosts
                            ?.affordability
                        }
                      />
                    </div>
                  )}
                  <div id="compare-colleges-icons" className="overflow-hidden">
                    <div
                      className={`
                        flex
                        flex-row
                        items-center
                        overflow-x-auto
                        ${favoriteLocal && "pl-2"}
                      `}
                    >
                      {collegesLocal.map((school, index) => (
                        <DraggableItem
                          key={`square-${school.school.ipedsId}`}
                          id={school.school.ipedsId}
                          moveItem={moveItem}
                        >
                          <AffordabilitySquare
                            key={index}
                            id="college-item-square"
                            className="cursor-pointer"
                            data-tooltip-content={school?.school?.name}
                            data-tooltip-id="college-thumbnail-squares"
                            coverage={
                              school.award?.postAwardSchoolCosts?.coverage
                            }
                            affordability={
                              school.award?.postAwardSchoolCosts?.affordability
                            }
                            onClick={() => navigateTo(index)}
                          />
                        </DraggableItem>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm font-bold normal-case">
                Reorder your schools using the square buttons above or drag
                cards, and star{" "}
                <span className="inline-block">
                  <SmallStar size={15} fillColor="fill-alternative-yellow" />
                </span>{" "}
                your favorites
              </p>
            </div>
          </div>
        </div>
        <div
          className="
            flex
            flex-1
            flex-row
            divide-x-2
            divide-black
            overflow-hidden
          "
        >
          {favoriteLocal && (
            <div className="py-3">
              <CollegeCard
                heights={heights}
                marginRight="mr-4"
                ethnicity={ethnicity}
                school={favoriteLocal}
                studentState={studentState}
                setHeights={setGlobalHeights}
                sectionsState={sectionsState}
                id="college-item-card-favorite"
                showUploadTutorial={showUploadTutorial}
                setSectionsState={setGlobalSectionState}
                selectAsFavorite={() => unselectAsFavoriteLocal()}
              />
            </div>
          )}
          <div
            ref={collegesContainerRef}
            className="overflow-x-auto max-w-full"
          >
            <ScrollingDropTarget containerRef={collegesContainerRef}>
              <div style={{ display: "flex", minWidth: "max-content" }}>
                <div
                  id="compare-colleges-items"
                  className={`
                  py-3
                  flex
                  flex-row
                  box-border
                  align-center
                  justify-start
                  overflow-x-auto
                  ${favoriteLocal && "pl-4"}
                `}
                >
                  {collegesLocal.map((school, index) => {
                    return (
                      <DraggableItem
                        key={school.school.ipedsId}
                        id={school.school.ipedsId}
                        moveItem={moveItem}
                      >
                        <CollegeCard
                          school={school}
                          heights={heights}
                          ethnicity={ethnicity}
                          studentState={studentState}
                          setHeights={setGlobalHeights}
                          sectionsState={sectionsState}
                          id={`college-item-card-${index}`}
                          setSectionsState={setGlobalSectionState}
                          showUploadTutorial={showUploadTutorial}
                          selectAsFavorite={(college) =>
                            selectAsFavoriteLocal(college)
                          }
                          marginRight={
                            index === collegesLocal.length - 1 ? "mr-0" : null
                          }
                        />
                      </DraggableItem>
                    );
                  })}
                </div>
              </div>
            </ScrollingDropTarget>
          </div>
        </div>
      </div>
      <Tooltip
        place="bottom"
        effect="solid"
        delayShow={500}
        id="college-thumbnail-squares"
        getContent={(schoolName) => {
          return (
            <div className="text-center">
              <p>{schoolName}</p>
              {schoolName !== favoriteLocal?.school?.name && (
                <p className="text-xs text-gray-500">(Drag to reorder)</p>
              )}
            </div>
          );
        }}
      />
      <Tooltip
        place="bottom"
        effect="solid"
        delayShow={500}
        id="enrollment-icon-label"
        getContent={(statusName) => {
          return (
            <div className="text-center">
              <p className="text-xsm font-bold capitalize">{statusName}</p>
            </div>
          );
        }}
      />
    </DndProvider>
  );
}

CompareColleges.propTypes = {
  colleges: PropTypes.array.isRequired,
  heights: PropTypes.object.isRequired,
  favorite: PropTypes.object,
  ethnicity: PropTypes.string,
  className: PropTypes.string.isRequired,
  studentState: PropTypes.string.isRequired,
  sectionsState: PropTypes.object.isRequired,
  selectAsFavorite: PropTypes.func.isRequired,
  setGlobalHeights: PropTypes.func.isRequired,
  unselectAsFavorite: PropTypes.func.isRequired,
  showUploadTutorial: PropTypes.bool.isRequired,
  setGlobalSectionState: PropTypes.func.isRequired,
  setNewSortOrderColleges: PropTypes.func.isRequired
};

export default CompareColleges;
